import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ApiError } from "../api";
import { set } from "../store";
import type { ProjectStatus, Render, SceneRole } from "../types";

// ------------------------------------------------------------------ data loading

export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const live = useRef(0);
  const load = useCallback(() => {
    const n = ++live.current;
    setLoading(true);
    fn()
      .then((d) => {
        if (n !== live.current) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (n !== live.current) return;
        if (e instanceof ApiError && e.status === 401) set({ auth: "out" });
        setError((e as Error).message);
      })
      .finally(() => n === live.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(load, [load]);
  return { data, setData, error, loading, reload: load };
}

// ------------------------------------------------------------------ formatting

export const fmtCredits = (n: number | null | undefined) => (n == null ? "–" : Math.round(n).toLocaleString("en-US"));
export const greeting = (name?: string) => {
  const h = new Date().getHours();
  const part = h < 5 ? "Late night" : h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
  return name ? `${part}, ${name}.` : `${part}.`;
};

export const fmtUsd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: n % 1 ? 2 : 0 });

export function timeAgo(iso?: string) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!Number.isFinite(s)) return "";
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 2) return "yesterday";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const AD_ROLES: Record<SceneRole, string> = { hook: "Hook", problem: "Problem", benefit: "Benefit", proof: "Proof", cta: "Call to action" };
/** Scene role names: the ad's, then any format's role id said plainly ("cold-open" -> "Cold open"). */
export const ROLE_LABEL: Record<string, string> = new Proxy(AD_ROLES as Record<string, string>, {
  get: (t, k: string) => t[k] ?? (typeof k === "string" ? k.replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase()) : undefined),
});

export const STEP_LABEL: Record<string, string> = {
  queued: "Waiting for a free renderer",
  images: "Painting the scenes",
  voiceover: "Recording the voiceover",
  voice: "Recording the voiceover",
  timeline: "Timing the cuts to the voice",
  render: "Rendering the video",
  done: "Finished",
};
export const stepLabel = (s: string | null) => (s ? (STEP_LABEL[s] ?? s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())) : "Starting");

export function statusOf(p: { status: ProjectStatus }, live?: Render): { label: string; tone: "draft" | "busy" | "done" | "bad" } {
  if (live && (live.status === "running" || live.status === "queued")) return { label: `Rendering ${Math.round(live.progress * 100)}%`, tone: "busy" };
  if (live?.status === "failed") return { label: "Render failed", tone: "bad" };
  if (live?.status === "done" || p.status === "done") return { label: "Ready to watch", tone: "done" };
  if (p.status === "rendering") return { label: "Rendering", tone: "busy" };
  if (p.status === "failed") return { label: "Render failed", tone: "bad" };
  if (p.status === "scripted") return { label: "Script ready", tone: "draft" };
  return { label: "Draft", tone: "draft" };
}

// ------------------------------------------------------------------ small pieces

/**
 * A bottom sheet on phones, a dialog on desktop. Focus moves into the dialog itself, or onto a button marked
 * `data-autofocus` (a confirm). It never jumps into a text field: that was the v2 library's keyboard popping up on
 * every tap (the item sheet focused its Name field). A field that should take focus says `data-autofocus` and only
 * gets it with a fine pointer (a mouse), never on a touch screen.
 */
export function Sheet({ title, onClose, children, wide = false, className = "" }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // 2026-11-02 (Keaton: a scene's sheet on a phone "snaps back" before the bottom): the focus and the page lock run
  // once, when the sheet opens. Keyed on onClose (a new function on every render of the page behind), they re-ran on
  // every update while the sheet was open: focus jumped to the button behind it and back, and iOS Safari scrolls to
  // what gets focus, so the sheet kept jumping back up while being scrolled.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const fine = window.matchMedia?.("(pointer: fine)").matches ?? false;
    const target = ref.current?.querySelector<HTMLElement>(fine ? "[data-autofocus]" : "button[data-autofocus]");
    (target ?? ref.current)?.focus({ preventScroll: true });
    const key = (e: KeyboardEvent) => e.key === "Escape" && close.current();
    window.addEventListener("keydown", key);
    document.body.classList.add("is-locked");
    return () => {
      window.removeEventListener("keydown", key);
      document.body.classList.remove("is-locked");
      prev?.focus?.({ preventScroll: true });
    };
  }, []);
  // rendered on <body>: a sheet opened from a fixed bar (the Director bar) must still sit above the tab bar
  return createPortal(
    <div className="sheet-scrim" onClick={onClose}>
      <div className={`sheet${wide ? " sheet-wide" : ""} ${className}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onClick={(e) => e.stopPropagation()} ref={ref}>
        <div className="sheet-grip" aria-hidden />
        <header className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <span className="spinner-dot" aria-hidden />
      {label && <span>{label}</span>}
    </span>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorNote({ text, retry }: { text: string; retry?: () => void }) {
  return (
    <div className="error-note" role="alert">
      <p>{text}</p>
      {retry && (
        <button type="button" className="btn btn-quiet" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

const ICONS: Record<string, ReactNode> = {
  home: <path d="M4 11.5 12 5l8 6.5V20h-5.5v-5h-5v5H4z" />,
  library: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
  brand: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 0 0 16c1.2 0 1.6-.9 1.2-1.8-.5-1-.1-2.2 1.1-2.2H17a3 3 0 0 0 3-3" />
      <circle cx="8.5" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="7.8" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="10" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  credits: (
    <>
      <ellipse cx="12" cy="7" rx="7" ry="3" />
      <path d="M5 7v5c0 1.7 3.1 3 7 3s7-1.3 7-3V7M5 12v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  expand: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  back: <path d="M15 5l-7 7 7 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  send: <path d="M5 12h13M13 6l6 6-6 6" />,
  play: <path d="M8 5.5v13l11-6.5z" fill="currentColor" />,
  download: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </>
  ),
  edit: <path d="M5 19h4L19 9l-4-4L5 15zM13.5 6.5l4 4" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  wave: <path d="M4 12h1M7 9v6M10 6v12M13 8v8M16 10v4M19 11v2" />,
  music: (
    <>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </>
  ),
  logout: <path d="M14 5h5v14h-5M10 8l-4 4 4 4M6 12h9" />,
  stop: <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />,
  pause: (
    <>
      <rect x="7" y="5.5" width="3.5" height="13" rx="1" fill="currentColor" stroke="none" />
      <rect x="13.5" y="5.5" width="3.5" height="13" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  redo: <path d="M19 8.5A7.5 7.5 0 1 0 19.5 14M19.5 4v4.8h-4.8" />,
  film: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M7.5 5v14M16.5 5v14M3.5 9.5h4M3.5 14.5h4M16.5 9.5h4M16.5 14.5h4" />
    </>
  ),
  image: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m4 17 5-4.5 4 3.5 2.5-2 4.5 3.5" />
    </>
  ),
  bolt: <path d="M13 3 5.5 13.5H12L11 21l7.5-10.5H12z" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="2.8" />
      <path d="M10.4 3.5h3.2l.5 2.3 1.6.9 2.2-.8 1.6 2.8-1.7 1.6v1.8l1.7 1.6-1.6 2.8-2.2-.8-1.6.9-.5 2.3h-3.2l-.5-2.3-1.6-.9-2.2.8-1.6-2.8 1.7-1.6v-1.8L4.5 8.7l1.6-2.8 2.2.8 1.6-.9z" />
    </>
  ),
  ticket: <path d="M4 7.5h16v3a1.5 1.5 0 0 0 0 3v3H4v-3a1.5 1.5 0 0 0 0-3zM14 7.5v9" strokeDasharray="0" />,
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
    </>
  ),
  script: <path d="M7 4h8l3 3v13H7zM10 10h5M10 13.5h5M10 17h3" />,
  "mic-off": (
    <>
      <path d="M9 9.2V8.5a3 3 0 0 1 6 0v3.5M14.6 14.1A3 3 0 0 1 9 12.5" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 10.9 4.8M18.5 11.5c0 .6-.1 1.2-.2 1.7M12 18v3M4 4l16 16" />
    </>
  ),
  chat: <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 4v-4h0A1.5 1.5 0 0 1 5 14.5z" />,
  compare: (
    <>
      <rect x="3.5" y="6" width="7.5" height="12" rx="1.5" />
      <rect x="13" y="6" width="7.5" height="12" rx="1.5" />
    </>
  ),
  restore: <path d="M5 12a7 7 0 1 0 2.1-5M5 4.5V9h4.5M12 8.5V12l2.5 2" />,
  chevron: <path d="m7 10 5 5 5-5" />,
  sliders: <path d="M5 7h9M18 7h1M5 17h3M12 17h7M16 5v4M10 15v4" />,
  volume: <path d="M5 10v4h3l4.5 3.5v-11L8 10zM16 9.5a3.5 3.5 0 0 1 0 5M18.5 7a7 7 0 0 1 0 10" />,
  mute: <path d="M5 10v4h3l4.5 3.5v-11L8 10zM16.5 9.5l4 5M20.5 9.5l-4 5" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  alert: <path d="M12 4 21 19H3zM12 10v4M12 16.5h.01" />,
  shield: <path d="M12 3.5 5 6.5v5.2c0 4.1 2.9 7.4 7 8.8 4.1-1.4 7-4.7 7-8.8V6.5z" />, // Wave 1 safety
  more: <path d="M6 12h.01M12 12h.01M18 12h.01" strokeWidth="2.6" />,
  filter: <path d="M4 6h16M7 12h10M10 18h4" />,
  stack: <path d="M12 4 20 8.5 12 13 4 8.5zM4 12.5l8 4.5 8-4.5M4 16.5l8 4.5 8-4.5" />,
  trash: <path d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12M10.5 11v5M13.5 11v5" />,
  sparkle: <path d="M12 4v4M12 16v4M4 12h4M16 12h4M7 7l2 2M15 15l2 2M17 7l-2 2M9 15l-2 2" />,
  bug: (
    <>
      <path d="M9 7.5a3 3 0 0 1 6 0" />
      <rect x="7" y="8" width="10" height="11" rx="5" />
      <path d="M12 11v8M4 12h3M17 12h3M5 17.5l2.2-1.2M19 17.5l-2.2-1.2M5.5 7.5l2 1.5M18.5 7.5l-2 1.5" />
    </>
  ),
};
export function Icon({ name, size = 20 }: { name: keyof typeof ICONS | string; size?: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      {ICONS[name]}
    </svg>
  );
}
